import snakeCase from 'lodash/snakeCase';
import { BadInputError } from '../errors/BadInputError';
import { ReferenceDataSchemaDefinition } from '../referenceData/ReferenceDataSchemaInterface';
import isNullish from '../utils/isNullish';
import { DeepReadonly, DeepReadonlyObject } from '../utils/utilTypes';
import {
  GROUP_ID_COL,
  VALUE_MAPPING_COLUMN_NAME_IS_MIGRATION_ENTRY,
  VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_TIME,
  VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_ID,
  VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_NAME,
  VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND,
  VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY,
  valueMappingUserUploadsZodSchema,
} from './hardcodedValueMappingConfigs';
import {
  MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED,
  MAPPINGS_APPROVE_TOOLTIP_DISABLED,
  MAPPINGS_SAVE_PROGRESS_TOOLTIP_ENABLED,
  MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED_FOR_APPROVAL,
  MAPPINGS_APPROVE_TOOLTIP_ENABLED,
  MAPPINGS_APPROVE_TOOLTIP_DISABLED_NO_ROWS,
  MAPPING_RESET_ALL_TOOLTIP_DISABLED,
  MAPPING_RESET_ALL_TOOLTIP_WITH_AUTOSAVE_ENABLED,
  MAPPINGS_APPROVE_TOOLTIP_PUBLISHED,
  InputValueMappingRecord,
  InputValueMappingRecords,
  MAPPING_NESTED_TREE_PATH_COL,
  SimpleValueMappingConfig,
  VALUE_MAPPING_VENDOR_COUNT_COL,
  ValueMappingColumn,
  ValueMappingColumnBase,
  ValueMappingConfig,
  ValueMappingOutputColumn,
  ValueMappingRecord,
  ValueMappingRecords,
  ValueMappingTableColumn,
  ValueMappingRow,
  ValueMappingRowUpdates,
  ValueMappingHistoryEntry,
  VALUE_MAPPING_VENDOR_SAMPLE_COL,
  NON_FALLBACK_ROW_COUNT_COL,
} from './valueMappingTypes';
import { z } from 'zod';
import { STABLE_ROW_ID_COL } from '../referenceData/stableIds';
import pick from 'lodash/pick';
import take from 'lodash/take';
import {
  GQReferenceDataRevisionState,
  GQTaskWatershedProcessStateSimplified,
  GQValueMappingOutputOption,
  GQValueMappingRow,
} from '../generated/graphql';
import groupBy from 'lodash/groupBy';
import { UnexpectedError } from '../errors/UnexpectedError';
import {
  firstNameLastInitial,
  nullableStringCompare,
} from '../utils/stringUtils';
import { sleep } from '../utils/sleep';
import { Immutable } from 'immer';
import {
  filterYielding,
  groupByYielding,
  mapYielding,
} from '../utils/yieldingLoopers';
import { DateTime } from 'luxon';
import {
  ValueMappingOutputValidationResult,
  ValueMappingOutputValidator,
} from './ValueMappingTableConfigurations';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { listFormatWithoutConjunction, pluralize } from '../utils/helpers';
import { getTableConfigurationForMappingConfigId } from './ValueMappingTableConfigurations';
import { ValueMappingConfigReader } from './ValueMappingConfigReader';
import { SqlList, sqlList } from '../sqlTemplateList';
import { sqlF, sqlIdentifier } from '../sqlTemplate';
import sortBy from 'lodash/sortBy';
import isNotNullish from '../utils/isNotNullish';

export const USEEIO_CODES_REF_DATA_SOURCE = 'financial_value_mapping_codes';
const VENDOR_SAMPLE_LONG_AMOUNT = 20;

export enum ValueMappingUserFlow {
  GROUP_ONE_READ_ONLY,
  GROUP_TWO_BETA,
  GROUP_THREE_LEGACY_MAPPING,
}

export function assertIsSimpleConfig(
  config: ValueMappingConfig
): asserts config is SimpleValueMappingConfig {
  if (config._type !== 'simple') {
    throw new BadInputError(
      `Encountered unsupported value mapping config type: ${config._type}`
    );
  }
}

/**
 * Returns a list of the key column names in the config.
 */
export function getValueMappingIdColumns(
  _config: ValueMappingConfig
): Array<string> {
  return [STABLE_ROW_ID_COL, GROUP_ID_COL];
}

/**
 * Returns a list of the fallback key column names in the config.
 */
export function getValueMappingFallbackKeyColumns(
  config: ValueMappingConfig
): Array<string> {
  const entries = Object.entries(config.keyColumns);
  return entries.filter(([k, v]) => v.isFallback).map(([k, v]) => k);
}

/**
 * Returns a list of the key column names in the config.
 */
export function getValueMappingKeyColumns(
  config: ValueMappingConfig
): Array<string> {
  return Object.keys(config.keyColumns);
}

/**
 * Returns a list of the output column names in the config.
 */
export function getValueMappingOutputColumns(
  config: ValueMappingConfig
): Array<string> {
  assertIsSimpleConfig(config);
  return Object.keys(config.outputColumns);
}

export function getFormattedValueMappingOutputColumns(
  config: ValueMappingConfig
): Array<ValueMappingTableColumn> {
  assertIsSimpleConfig(config);
  return formatColumns(config.outputColumns);
}

/**
 * Returns a list of the output column names in the config.
 */
export function getValueMappingOutputColumnsEntries(
  config: ValueMappingConfig
): Array<[string, DeepReadonlyObject<ValueMappingOutputColumn>]> {
  assertIsSimpleConfig(config);
  return Object.entries(config.outputColumns);
}

/**
 * Returns a list of the context column names in the config.
 */
export function getValueMappingContextColumns(
  config: ValueMappingConfig
): Array<string> {
  return Object.keys(config.contextColumns);
}

/**
 * Returns a list of the writable columns in the config that are cumulative.
 */
export function getValueMappingCumulativeColumns(
  config: ValueMappingConfig,
  writableColumns: Array<string>
): Array<string> {
  return Object.entries(config.contextColumns)
    .filter(([nom, col]) => writableColumns.includes(nom) && col.isCumulative)
    .map(([nom, _col]) => nom);
}

/**
 * Returns a list of the writable columns in the config that will be aggregated to a minimum.
 */
export function getValueMappingMinimumColumns(
  config: ValueMappingConfig,
  writableColumns: Array<string>
): Array<string> {
  return Object.entries(config.contextColumns)
    .filter(([nom, col]) => writableColumns.includes(nom) && col.isMinimum)
    .map(([nom, _col]) => nom);
}

/**
 * Returns a list of the writable columns in the config that will be aggregated to a maximum.
 */
export function getValueMappingMaximumColumns(
  config: ValueMappingConfig,
  writableColumns: Array<string>
): Array<string> {
  return Object.entries(config.contextColumns)
    .filter(([nom, col]) => writableColumns.includes(nom) && col.isMaximum)
    .map(([nom, _col]) => nom);
}

function getHiddenColumnNames(
  columns: DeepReadonlyObject<Record<string, ValueMappingColumnBase>>
): Set<string> {
  const hiddenColumnNames = Object.entries(columns)
    .filter(([_, column]) => column.isHiddenByDefault === true)
    .map(([columnName, _]) => columnName);

  return new Set(hiddenColumnNames);
}

function formatColumns(
  columns: DeepReadonlyObject<Record<string, ValueMappingColumnBase>>
) {
  return Object.entries(columns).map(([columnName, columnData]) => {
    return {
      name: columnName,
      canonicalSchemaColumn: columnData,
    };
  });
}

function filterColumns(
  columns: DeepReadonlyObject<Record<string, ValueMappingColumnBase>>
) {
  const hiddenColumnNames = getHiddenColumnNames(columns);

  return formatColumns(columns).filter(
    (columnData) => !hiddenColumnNames.has(snakeCase(columnData.name))
  );
}

/**
 * This filters from key columns any special columns that are used behind the scenes.
 * The results are only the columns a user would see in the value mapping tables.
 */
export function getValueMappingDisplayedKeyColumns(
  config: ValueMappingConfig
): Array<ValueMappingTableColumn> {
  return filterColumns(config.keyColumns);
}

/**
 * This filters from context columns any special columns that are used behind the scenes.
 * The results are only the columns a user would see in the value mapping tables.
 */
export function getValueMappingDisplayedContextColumns(
  config: ValueMappingConfig
): Array<ValueMappingTableColumn> {
  return filterColumns(config.contextColumns);
}

/**
 * This filters from output columns any special columns that are used behind the scenes.
 * The results are only the columns a user would see in the value mapping tables.
 */
export function getValueMappingDisplayedOutputColumns(
  config: ValueMappingConfig,
  selectedOutputOption?: string | undefined
): Array<ValueMappingTableColumn> {
  // TODO: support multi-options by checking ._type
  assertIsSimpleConfig(config);
  return filterColumns(config.outputColumns);
}

/**
 * Sets null on non required keyColumns.
 */
export function validateAndPadKeyColumns(
  config: ValueMappingConfig,
  records: ValueMappingRecords | InputValueMappingRecords
): void {
  const keyColumns = getValueMappingKeyColumns(config);
  const contextColumns = getValueMappingContextColumns(config);
  const outputColumns = getValueMappingOutputColumns(config);
  const requiredKeyColumns = new Set(
    Object.entries(config.keyColumns)
      .filter(([_k, v]) => v.isRequired)
      .map(([k, _v]) => k)
  );

  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    for (const keyColumn of keyColumns) {
      if (isNullish(record[keyColumn]) && requiredKeyColumns.has(keyColumn)) {
        throw new BadInputError(
          `Record is missing required key column "${keyColumn}": ${JSON.stringify(
            record
          )}`
        );
      }
      record[keyColumn] ??= null;
    }
    records[i] = pick(record, [
      ...keyColumns,
      ...contextColumns,
      ...outputColumns,
    ]);
  }
}

/**
 * Returns a flattened version of the config where all columns are at the
 * top-level.
 */
export function flattenValueMappingConfig(
  config: ValueMappingConfig
): DeepReadonly<Record<string, ValueMappingColumn>> {
  assertIsSimpleConfig(config);

  return {
    ...config.keyColumns,
    ...config.contextColumns,
    ...config.outputColumns,
  };
}

/**
 * Converts an InputValueMapping record (which can contain objects) to a
 * ValueMappingRecord by validating objects against Zod schemas defined in the
 * config and serializing objects to JSON.
 */
export function inputToValueMappingRecord(
  config: ValueMappingConfig,
  record: InputValueMappingRecord
): ValueMappingRecord {
  const res: ValueMappingRecord = {};
  const flatConfig = flattenValueMappingConfig(config);

  for (const [key, val] of Object.entries(record)) {
    if (flatConfig[key] && flatConfig[key].format === 'date') {
      res[key] = z.coerce.date().nullable().parse(val);
    } else if (typeof val === 'object' && !isNullish(val)) {
      const zodObject = flatConfig[key].zod;
      BadInputError.invariant(zodObject, `Missing Zod schema for key: ${key}`);
      res[key] = JSON.stringify(zodObject.parse(val));
    } else if (
      !flatConfig[key] &&
      key !== GROUP_ID_COL &&
      key !== STABLE_ROW_ID_COL
    ) {
      throw new BadInputError(
        `Found a key that is not a special reference data column, and does not exist in config: "${key}".`
      );
    } else {
      res[key] = val;
    }
  }

  return res;
}

/**
 * Validates a list of options across multiple rows:
 * - Invalid options are identified if they fail the validation in **ANY** row.
 * - Warnings are issued for options that aren't invalid, but have a warning for
 *   any row.
 *
 * @returns a ValueMappingOutputValidationResult for use in filtering/warning
 * the given options.
 */
export function bulkValidateMappingOutputOptions({
  rowsSelected,
  validateOutputOptions,
  options,
}: {
  rowsSelected: Array<ValueMappingRow> | undefined;
  validateOutputOptions: ValueMappingOutputValidator | undefined;
  options: Array<GQValueMappingOutputOption>;
}): ValueMappingOutputValidationResult | undefined {
  if (!validateOutputOptions || !rowsSelected) {
    return undefined;
  }

  const validations = rowsSelected
    .map((row) => {
      return validateOutputOptions(row, options);
    })
    .filter((v): v is ValueMappingOutputValidationResult => v !== undefined);

  // Now we've got a list of validations, and need to combine them into a single validation:
  // - if an option is invalid for any one row, it's invalid for all rows
  // - if a warning exists for any one row, it's a warning for all rows
  const allInvalidOptionIds: Array<string> = [];
  const allWarnings: ValueMappingOutputValidationResult['warnings'] = [];
  validations.forEach((v) => {
    allInvalidOptionIds.push(...v.invalidOptions);
    allWarnings.push(...v.warnings);
  });

  const invalidOptions = uniq(allInvalidOptionIds);
  // We only need to unique by optionID, any warning message will do. If a
  // warning was in the invalid list, we also want to remove it.
  const warnings = uniqBy(allWarnings, (w) => w.outputOptionId).filter(
    (warning) => !invalidOptions.includes(warning.outputOptionId)
  );

  return {
    invalidOptions,
    warnings,
  };
}

/**
 * Converts a ValueMappingConfig to its corresponding reference data schema.
 * If isForPublish, make mapping outputs with isRequiredForPublish required fields.
 */
export function valueMappingConfigToRefSchema(
  config: ValueMappingConfig
): ReferenceDataSchemaDefinition {
  assertIsSimpleConfig(config);

  const uniqueKeys: Array<string> = [];
  const required: Array<string> = [GROUP_ID_COL];
  const schema: ReferenceDataSchemaDefinition = {
    $id: 'https://watershedclimate.com/valuemapping/v1.schema.json',
    $schema: 'http://watershedclimate.com/canonical/v0.01/schema#',
    title: config.name,
    description: `ValueMappingConfig "${config.name}" translated to a ReferenceDataSchemaDefinition.`,
    type: 'object',
    properties: {
      [GROUP_ID_COL]: {
        type: 'string',
        description:
          'Hashed ID across non-date, non-override ValueMapping keyColumns.',
      },
    },
    required,
    uniqueKeys,
    standardizers: [],
    standardizerOutputOnly: [],
  };

  for (const columnName of Object.keys(config.keyColumns)) {
    schema.properties[columnName] = config.keyColumns[columnName];
    if (config.keyColumns[columnName].isRequired) {
      required.push(columnName);
    }
    uniqueKeys.push(columnName);
  }

  for (const columnName of Object.keys(config.outputColumns)) {
    schema.properties[columnName] = config.outputColumns[columnName];
  }

  return schema;
}

/**
 * Generates the reference data source name backing a value mapping artifact.
 */
export function generateRefSourceName(configId: string): string {
  return `value_mappings_${configId}`;
}

/**
 * Returns keyColulmns used for generating group ids
 */
export function groupKeyColumns(config: ValueMappingConfig): Array<string> {
  /**
   * Include key columns that are
   * - not fallbacks (e.g. vendor_override)
   * - not a time field
   */
  const groupKeyColumns = Object.entries(config.keyColumns).filter(
    ([key, entry]) =>
      !entry.isFallback &&
      !entry.format?.includes('date') &&
      !entry.format?.includes('epoch') &&
      !key.includes('date')
  );
  return groupKeyColumns.map(([key, _entry]) => key);
}

const setCellSchema = z.array(z.unknown());
export function parseSetCell(value: unknown): Array<unknown> {
  if (typeof value !== 'string') {
    throw new BadInputError('Set cell value must be a JSON array', {
      data: { value },
    });
  }
  const parsed = setCellSchema.safeParse(JSON.parse(value));
  if (!parsed.success) {
    throw new BadInputError(
      'Unexpected value for set cell: expected a JSON array',
      { cause: parsed.error, data: { value } }
    );
  }
  return parsed.data;
}

/**
 * Flattens an array of the contents of an isSet column
 * to a Set<string>.
 */
export function flattenSetCellsIntoStringSet(
  values: Array<unknown>
): Set<string> {
  const mySet = new Set<string>();
  values.forEach((value) => {
    if (typeof value !== 'string') {
      throw new BadInputError('Set cell value must be a JSON array', {
        data: { value },
      });
    }
    const parsedValues = parseSetCell(value);

    if (parsedValues !== null) {
      parsedValues.forEach((v) => {
        if (typeof v !== 'string') {
          throw new BadInputError(
            'Set cell value must be a JSON array of strings!',
            {
              data: { v },
            }
          );
        }
        mySet.add(v);
      });
    }
  });
  return mySet;
}

export function setToJsonArray(mySet: Set<string>): string {
  return JSON.stringify(Array.from(mySet));
}

/**
 * Important: This function assumes records is the output of aggregateSetAndCumulativeColumns.
 * Converts ValueMappingRecords to FE ready GQValueMappingRows
 */
export async function recordToGQValueMappingRow({
  config,
  latestUpdateLookup,
  orgHasVendorOverrides,
  orgHasVendorPreviews,
  orgHasOptimizedAggregation,
  projectIdsForRecords,
  records,
  valueMappingConfigId,
}: {
  records: Immutable<Array<ValueMappingRecord>>;
  latestUpdateLookup: Immutable<
    Map<
      string,
      {
        isLegacyMigration: boolean;
        editor: { name: string; isWatershedEmployee: boolean };
        entry: Pick<ValueMappingHistoryEntry, 'changedAt' | 'changedBy'>;
      }
    >
  >;
  orgHasVendorOverrides: boolean;
  orgHasVendorPreviews?: boolean;
  orgHasOptimizedAggregation: boolean;
  config: ValueMappingConfig;
  projectIdsForRecords?: Immutable<Map<ValueMappingRecord, Set<string>>>;
  valueMappingConfigId: string;
}): Promise<Array<GQValueMappingRow>> {
  const mappingConfiguration =
    getTableConfigurationForMappingConfigId(valueMappingConfigId);

  const configHasVendorOverrides =
    mappingConfiguration.hasVendorOverrides || false;
  // Has vendor overrides if BOTH config AND org allow it.
  const shouldUseVendorOverrides =
    (configHasVendorOverrides && orgHasVendorOverrides) || false;

  // 1. Group related records (all records that have the same group ID), including:
  // - vendors for same key columns but with different or null vendor id's
  // - vendors with same key columns but different start/end dates
  const groupedById = await groupByYielding(records, (record) => {
    return record[GROUP_ID_COL] as string;
  });

  // TODO (kristy): integrate aggregateSetAndCumulativeColumns into this function
  // For every record...
  const valueMappingRows = await mapYielding(records, (record) => {
    const stableRowId = record[STABLE_ROW_ID_COL];
    const groupId = record[GROUP_ID_COL];
    const projectIds = projectIdsForRecords
      ? projectIdsForRecords.get(record)
      : null;
    UnexpectedError.invariant(
      typeof stableRowId === 'string',
      `${STABLE_ROW_ID_COL} must be provided and a string`
    );
    UnexpectedError.invariant(
      typeof groupId === 'string',
      `${GROUP_ID_COL} must be provided and a string`
    );
    // Clone record and add in some additional data for FE processing
    const baseCurrentRecord: InputValueMappingRecord = projectIds
      ? {
          ...record,
          id: stableRowId,
          groupId,
          projectIds: Array.from(projectIds),
        }
      : { ...record, id: stableRowId, groupId };

    const relatedRecords = groupedById[groupId];
    const latestUpdate = latestUpdateLookup.get(stableRowId);

    // 2. Add changelog info
    if (latestUpdate !== undefined) {
      baseCurrentRecord[VALUE_MAPPING_COLUMN_NAME_IS_MIGRATION_ENTRY] =
        latestUpdate.isLegacyMigration;
      baseCurrentRecord[VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_NAME] =
        `${firstNameLastInitial(latestUpdate.editor.name)}${
          latestUpdate.editor.isWatershedEmployee ? ' - Watershed' : ''
        }`;
      baseCurrentRecord[VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_ID] =
        latestUpdate.entry.changedBy;
      baseCurrentRecord[VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_TIME] =
        latestUpdate.entry.changedAt;
    }

    // 3. Optional: Handle vendor overrides grouping and counts if the config has feature on
    // NOTE: this is somewhat configured for financials (see the sorting piece)
    // If a new config is using vendor overrides, they must add their own sorting!
    if (configHasVendorOverrides) {
      const vendorEntity =
        record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY];

      // subtract the fallback row with null vendor
      const vendorOverrideRowCount = relatedRecords.length - 1;
      UnexpectedError.invariant(
        vendorOverrideRowCount > -1,
        `No null vendor row found for Group ID (${GROUP_ID_COL})`
      );

      // Add the count of child vendors only to parent (fallback) rows
      if (isNotNullish(record[NON_FALLBACK_ROW_COUNT_COL])) {
        baseCurrentRecord[VALUE_MAPPING_VENDOR_COUNT_COL] =
          baseCurrentRecord[NON_FALLBACK_ROW_COUNT_COL];
      }

      // If the org is in the dropdown vendor feature flag, provide grouping data
      if (orgHasVendorOverrides) {
        if (vendorEntity == null) {
          baseCurrentRecord[MAPPING_NESTED_TREE_PATH_COL] = [groupId];
        } else {
          baseCurrentRecord[MAPPING_NESTED_TREE_PATH_COL] = [
            groupId,
            record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY],
          ];
        }
      } else if (orgHasVendorPreviews) {
        // This sort is a temporary hack while server side pagination is built, relying on the fact that
        // defaultSorting is only being used by financials right now.
        // If optimized aggregation (which does sorting already) is off, and defaultSorting exists,
        // we're sorting by decreasing amount spend. This will go away by 1/30/2024
        const sortedRecords =
          !orgHasOptimizedAggregation && mappingConfiguration.defaultSorting
            ? sortBy(relatedRecords, (rec) => {
                const spend =
                  rec[
                    VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND
                  ];
                return spend == null ? 0 : -Math.abs(spend as number);
              })
            : relatedRecords;
        const firstVendorsForParent = take(
          sortedRecords,
          VENDOR_SAMPLE_LONG_AMOUNT
        );

        // Track names of the vendors (excluding the null fallback if in the list)
        // NOTE: this will be 10 names or 9 if the fallback is in the list because
        // we are already limiting vendors to 10 per group id in the back end.
        // This is rapidly changing as we implement server side pagination.
        const vendorNames = firstVendorsForParent
          .map(
            (row) =>
              row[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY]
          )
          .filter((name) => name != null);
        baseCurrentRecord[VALUE_MAPPING_VENDOR_SAMPLE_COL] = vendorNames;
      }
    } else {
      baseCurrentRecord[MAPPING_NESTED_TREE_PATH_COL] = [
        groupId,
        record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY],
      ];
    }

    delete baseCurrentRecord[NON_FALLBACK_ROW_COUNT_COL];

    // TODO: at this point this structure should be rewritten, but wait until we're sure
    // what structure we want from measurements/vendor overrides/etc.
    return {
      // id necessary key for graphql rows
      id: stableRowId,
      vendorEntity:
        record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY], // used for final vendor override filter
      baseRowJson: JSON.stringify(baseCurrentRecord),
    };
  });

  // 5. If vendor overrides are not enabled, filter out rows with vendor entities.
  // We need to do this at the very end to make sure we accumulated amount_spend correctly.
  // Do this ONLY if the config has vendor overrides
  const filteredRows = mappingConfiguration.hasVendorOverrides
    ? await filterYielding(
        valueMappingRows,
        (row) => shouldUseVendorOverrides || row.vendorEntity === null
      )
    : valueMappingRows;

  return mapYielding(filteredRows, (row) => ({
    id: row.id,
    baseRowJson: row.baseRowJson,
  }));
}

function getVendorsListString(
  vendorNames: Array<string>,
  totalCount: number,
  sampleSize: number
): string {
  // Will take sampleSize or if total is shorter, will keep full list
  const shortList = take(vendorNames, sampleSize);
  const remainingCount = totalCount - shortList.length;
  const othersString =
    remainingCount === 0 ? '' : ` and ${pluralize(remainingCount, 'other')}`;

  return `${listFormatWithoutConjunction(shortList)}${othersString}`;
}

/**
 * This only gets called for the supplier column on parent (fallback) rows (thus, null supplier).
 */
export function getValuesForVendorOverridesColumn(
  hasOrgAndConfigVendorOverrides: boolean,
  row: ValueMappingRow,
  vendorsCount: number,
  shouldUseVendorPreviews: boolean
): Array<string> {
  const vendorSampleList = row[VALUE_MAPPING_VENDOR_SAMPLE_COL];

  // If the fallback row has no suppliers, return simple strings
  if (
    vendorsCount === 0 ||
    (!hasOrgAndConfigVendorOverrides &&
      // this check only applies when vendor overrides is off
      (vendorSampleList == null || vendorSampleList.length === 0))
  ) {
    return ['-', 'No suppliers apply to this row'];
  }

  if (hasOrgAndConfigVendorOverrides) {
    return [
      pluralize(vendorsCount, 'supplier'),
      'Click the arrow to view top suppliers',
    ];
  } else if (!shouldUseVendorPreviews) {
    return [pluralize(vendorsCount, 'supplier'), ''];
  }

  // This shouldn't happen, but if for some reason someone calls this without loading the data
  // correctly, it won't break and will just appear empty.
  if (!vendorSampleList) {
    return ['', ''];
  }

  // TODO: If do not have vendor overrides turned on, show the full names

  const shortString = getVendorsListString(
    vendorSampleList,
    vendorsCount,
    5 // take 5 top of the list
  );

  const longString = getVendorsListString(
    vendorSampleList,
    vendorsCount,
    VENDOR_SAMPLE_LONG_AMOUNT
  );

  return [shortString, longString];
}

export const VALUE_MAPPING_REQUEST_SUGGESTIONS_DATA_ISSUE_TITLE =
  '[AUTOGENERATED] Request suggestions';

/**
 * Returns records with aggregated set and cumulative columns for null fallback
 * rows.
 */
export async function aggregateSetAndCumulativeColumns(
  records: Immutable<ValueMappingRecords>,
  config: ValueMappingConfig
): Promise<Array<ValueMappingRecord>> {
  const groupedById = groupBy(records, (record) => {
    return record[GROUP_ID_COL];
  });
  const fallbackKeyCols = getValueMappingFallbackKeyColumns(config);
  const result: Array<ValueMappingRecord> = [];
  let loopIndex = 0;
  for (const record of records) {
    if (++loopIndex % 20 === 0) {
      // Avoid blocking the main thread if we're dealing with 1000's of records.
      await sleep(0);
    }
    const groupId = record[GROUP_ID_COL];
    UnexpectedError.invariant(
      typeof groupId === 'string',
      `${GROUP_ID_COL} must be provided and a string`
    );
    const relatedRecords = groupedById[groupId];

    // if the row is not a null fallback row
    if (!fallbackKeyCols.map((col) => record[col]).includes(null)) {
      result.push({ ...record, [NON_FALLBACK_ROW_COUNT_COL]: null });
      continue;
    }

    const baseCurrentRecord: ValueMappingRecord = {
      ...record,
    };

    const contextColumns = Object.entries(config.contextColumns).map(
      ([nom, col]) => nom
    );

    const cumulativeColumns = getValueMappingCumulativeColumns(
      config,
      contextColumns
    );

    const minimumColumns = getValueMappingMinimumColumns(
      config,
      contextColumns
    );

    const maximumColumns = getValueMappingMaximumColumns(
      config,
      contextColumns
    );

    for (const col of cumulativeColumns) {
      baseCurrentRecord[col] = relatedRecords.reduce(
        (acc, curr) => acc + (curr[col] as number), // includes the current row we are inspecting
        0
      );
    }

    for (const col of minimumColumns) {
      // this only works for dates right now, but it should be fine since we're moving to duckdb
      const colValues: Array<DateTime> = relatedRecords.flatMap(
        ({ col: colValue }) => {
          if (colValue instanceof Date) {
            return [DateTime.fromJSDate(colValue)];
          }
          return [];
        }
      );
      if (colValues.length === 0) {
        baseCurrentRecord[col] = null;
        continue;
      }
      baseCurrentRecord[col] = DateTime.min(...colValues).toJSDate();
    }

    for (const col of maximumColumns) {
      // this only works for dates right now, but it should be fine since we're moving to duckdb
      const colValues: Array<DateTime> = relatedRecords.flatMap(
        ({ col: colValue }) => {
          if (colValue instanceof Date) {
            return [DateTime.fromJSDate(colValue)];
          }
          return [];
        }
      );
      if (colValues.length === 0) {
        baseCurrentRecord[col] = null;
        continue;
      }
      baseCurrentRecord[col] = DateTime.max(...colValues).toJSDate();
    }

    const setColumns = Object.entries(config.contextColumns)
      .filter(([_nom, col]) => col.isSet)
      .map(([nom]) => nom);

    for (const col of setColumns) {
      baseCurrentRecord[col] = setToJsonArray(
        flattenSetCellsIntoStringSet(
          relatedRecords.flatMap((r) => r[col] ?? [])
        )
      );
    }
    result.push({
      ...baseCurrentRecord,
      [NON_FALLBACK_ROW_COUNT_COL]: relatedRecords.length - 1,
    });
  }
  return result;
}

/**
 * Returns a list of SQL expressions that can be used to aggregate the context columns
 */
export function getAccumulationColumnsSql(
  config: ValueMappingConfigReader
): SqlList {
  // If this is a financials config, for context columns that are not cumulative in some other way,
  // we should sort by the vendor entity to get the value where it is null to get the value
  const sortByColumn = config.fallbackKeyColumns.has(
    VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY
  )
    ? VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY
    : null;
  const sortGenericContextColumnSql = sortByColumn
    ? sqlF` order by ${sqlIdentifier(sortByColumn)} NULLS FIRST`
    : sqlF``;
  return sqlList().addMapped(
    Object.entries(config.raw.contextColumns),
    ([colName, colDef]) => {
      const c = sqlIdentifier(colName);
      if (colDef.isCumulative) {
        return sqlF`sum(${c}) AS ${c}`;
      }
      if (colDef.isSet) {
        return sqlF`list(${c}::JSON::TEXT[]).flatten().list_distinct().to_json() AS ${c}`;
      }
      if (colDef.isMinimum) {
        return sqlF`min(${c}) AS ${c}`;
      }
      if (colDef.isMaximum) {
        return sqlF`max(${c}) AS ${c}`;
      }
      return sqlF`first(${c} ${sortGenericContextColumnSql}) AS ${c}`;
    }
  );
}

/**
 * Returns:
 *
 * - A Map from value mapping record (the object reference) to a parsed Set of
 *   its UUT FMD IDs.
 * - A Set of all the distinct UUT FMD IDs in the input records.
 *
 * If the value mapping config doesn't include a
 * `VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS` column, the returned
 * objects are empty.
 *
 * Most of the way this is implemented is based on trying to do as little work
 * as necessary (e.g. not JSON-and-Zod-parsing more than needed), because we
 * might be looping through 1000's of records.
 */
export async function uutFmdIdsForRecords<
  T extends DeepReadonly<{
    [VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS]?: string | null;
  }>,
>(
  records: ReadonlyArray<T>
): Promise<{
  // The UUT FMD ID Sets are reused for efficiency (same memory reference for
  // the same set); they're typed as readonly, then, so the consumer can't
  // inadvertently mutate with surprising consequences.
  recordToUutFmdIdSet: Map<T, ReadonlySet<string>>;
  distinctUutFmdIds: Set<string>;
}> {
  const recordToUutFmdIdSet = new Map<T, Set<string>>();
  const distinctUutFmdIds = new Set<string>();
  const uutFmdIdSetCache = new Map<string, Set<string>>();

  let loopIndex = 0;
  for (const record of records) {
    if (++loopIndex % 20 === 0) {
      // Yield to the event loop to avoid event loop blockages.
      await sleep(0);
    }
    const jsonArray =
      record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS];
    if (jsonArray == null) {
      recordToUutFmdIdSet.set(record, new Set());
      continue;
    }
    const cached = uutFmdIdSetCache.get(jsonArray);
    if (cached) {
      recordToUutFmdIdSet.set(record, cached);
      continue;
    }
    const parsedArray = valueMappingUserUploadsZodSchema.parse(
      JSON.parse(jsonArray)
    );
    if (parsedArray === null) {
      continue;
    }
    const uutFmdIdSet = new Set(parsedArray);
    uutFmdIdSetCache.set(jsonArray, uutFmdIdSet);
    recordToUutFmdIdSet.set(record, uutFmdIdSet);
    for (const fmdId of parsedArray) {
      distinctUutFmdIds.add(fmdId);
    }
  }

  return { recordToUutFmdIdSet, distinctUutFmdIds };
}

export async function convertInputVMRecordToVMRecord(
  config: ValueMappingConfig,
  records: InputValueMappingRecords
): Promise<Array<ValueMappingRecord>> {
  return await mapYielding(records, (record) =>
    inputToValueMappingRecord(config, record)
  );
}

export function getValueMappingSaveApproveStatusAndTooltips({
  config,
  dataRows,
  userMappingChanges,
  revisionState,
  isSavingProgress,
  isApprovingAll,
  isResetEnabled,
  externalApproveTooltip,
  externalSaveTooltip,
  isApproveAllAllowed,
  rowsMetadata,
  valueMappingTaskStatus,
  isRowOptional,
}: {
  config: ValueMappingConfig;
  dataRows: Array<ValueMappingRow>;
  userMappingChanges: ValueMappingRowUpdates;
  revisionState: GQReferenceDataRevisionState | null;
  isSavingProgress: boolean;
  isApprovingAll: boolean;
  isResetEnabled: boolean | undefined;
  externalApproveTooltip?: string;
  externalSaveTooltip?: string;
  isApproveAllAllowed: boolean;
  rowsMetadata?: { allRowsAreFilled: boolean };
  valueMappingTaskStatus?: GQTaskWatershedProcessStateSimplified;
  isRowOptional?: (row: ValueMappingRow) => boolean;
}): {
  isSaveEnabled: boolean;
  saveTooltip: string | undefined;
  isApproveEnabled: boolean;
  approveTooltip: string | undefined;
  resetAllTooltip: string | undefined;
} {
  const requiredForPublishedColumns = getValueMappingOutputColumnsEntries(
    config
  )
    .filter(
      ([name, columnConfig]) => columnConfig.isRequiredForPublish === true
    )
    .map(([name]) => name);

  let allRowsAreFilled = true;
  if (rowsMetadata) {
    allRowsAreFilled = rowsMetadata.allRowsAreFilled;
  } else {
    for (const dataRow of dataRows) {
      // Skip checking that required columns are filled for optional rows
      if (isRowOptional && isRowOptional(dataRow)) {
        continue;
      }
      // If should_exclude filled, that also counts as filling the row, no need to check other cols
      if (dataRow[VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE] === true) {
        continue;
      }

      // If not excluded, all required columns must be filled out to approve
      if (!requiredForPublishedColumns.every((col) => dataRow[col] != null)) {
        allRowsAreFilled = false;
        break;
      }
    }
  }
  const hasUnsavedChanges = Object.entries(userMappingChanges).length > 0;

  // Now we figure out how to render the save and approve buttons. Each can be
  // enabled or disabled, and may or may not have tooltip text, depending on
  // which state the data is in -- as described below.
  let isSaveEnabled: boolean;
  let saveTooltip: string | undefined;
  let isApproveEnabled: boolean;
  let approveTooltip: string | undefined;
  if (dataRows.length === 0) {
    // STATE 1: No rows at all.
    isSaveEnabled = false;
    saveTooltip = MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED;
    isApproveEnabled = false;
    approveTooltip = MAPPINGS_APPROVE_TOOLTIP_DISABLED_NO_ROWS;
  } else if (!allRowsAreFilled) {
    // STATE 2: Not all rows are filled out
    isSaveEnabled = false;
    saveTooltip = MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED;
    isApproveEnabled = false;
    approveTooltip = MAPPINGS_APPROVE_TOOLTIP_DISABLED;
  } else if (hasUnsavedChanges) {
    // STATE 3: Unsaved changes.
    isSaveEnabled = true;
    saveTooltip = MAPPINGS_SAVE_PROGRESS_TOOLTIP_ENABLED;
    isApproveEnabled = false;
    approveTooltip = MAPPINGS_APPROVE_TOOLTIP_DISABLED;
  } else if (revisionState === GQReferenceDataRevisionState.Published) {
    // STATE 4: The current state has been both saved and approved.
    isSaveEnabled = false;
    saveTooltip = MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED;
    isApproveEnabled = valueMappingTaskStatus
      ? // Allow approving again if there is a VM task and it is not yet complete
        valueMappingTaskStatus !==
        GQTaskWatershedProcessStateSimplified.Completed
      : false;
    approveTooltip = MAPPINGS_APPROVE_TOOLTIP_PUBLISHED;
  } else {
    // STATE 5: The current state has been saved but not yet been approved.
    isSaveEnabled = false;
    saveTooltip = MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED_FOR_APPROVAL;
    isApproveEnabled = true;
    approveTooltip = MAPPINGS_APPROVE_TOOLTIP_ENABLED;
  }

  // OVERRIDES: Don't show a tooltip if the action is in progress.
  if (isSavingProgress) {
    saveTooltip = undefined;
  }
  if (isApprovingAll) {
    approveTooltip = undefined;
  }

  let resetAllTooltip = MAPPING_RESET_ALL_TOOLTIP_DISABLED;
  if (isResetEnabled) {
    resetAllTooltip = MAPPING_RESET_ALL_TOOLTIP_WITH_AUTOSAVE_ENABLED;
  }

  // Customize the approve all state based on the external caller's settings.
  if (externalApproveTooltip) {
    approveTooltip = externalApproveTooltip;
  }
  if (!isApproveAllAllowed) {
    isApproveEnabled = false;
  }
  if (externalSaveTooltip) {
    saveTooltip = externalSaveTooltip;
  }

  return {
    isSaveEnabled,
    saveTooltip,
    isApproveEnabled,
    approveTooltip,
    resetAllTooltip,
  };
}

/**
 * Returns uut fmd ids of rows with populated mappings.
 */
export async function getUutFmdIdsOfPopulatedMappingsUtil(
  records: ValueMappingRecords,
  config: ValueMappingConfig
): Promise<Set<string>> {
  // 0. aggregate so that null fallback rows have the rolled up user_upload_fmds
  const aggregatedRecords = await aggregateSetAndCumulativeColumns(
    records,
    config
  );

  // 1. filter to rows that are populated
  const populatedRecords = await filterYielding(
    aggregatedRecords,
    (record) =>
      record[VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA] !== null ||
      record[VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE] === true
  );

  // 2. flatten the fmd id column
  const { distinctUutFmdIds } = await uutFmdIdsForRecords(populatedRecords);

  return distinctUutFmdIds;
}

export function formatUserNameForChangelog(
  user: {
    name: string;
    isWatershedEmployee: boolean;
    isWatershedContractor: boolean;
  },
  isMigrationEntry?: boolean
): string {
  if (isMigrationEntry) {
    return 'Migrated from previous mappings';
  }
  return `${firstNameLastInitial(user.name)}${
    user.isWatershedEmployee || user.isWatershedContractor ? ' - Watershed' : ''
  }`;
}

// Returns latest version of a config, overall.
export function getLatestValueMappingConfigVersion(
  configs: Array<ValueMappingConfig>
): ValueMappingConfig {
  // Note that a string sort on liveAsOf will help us get the latest version.
  // e.g. '2023-01-02.1' < '2023-01-02.2' < '2024-01-01.1'
  const configVersionsSorted = configs.sort((a, b) =>
    nullableStringCompare(a.liveAsOf, b.liveAsOf)
  );
  const latestVersion = configVersionsSorted[configVersionsSorted.length - 1];
  if (configVersionsSorted.length === 0 || latestVersion.liveAsOf === null) {
    throw new BadInputError('Could not find a live config', {
      data: { configs },
    });
  }
  return latestVersion;
}

export type ValueMappingFilterType =
  | 'flagged'
  | 'incomplete'
  | 'required'
  | 'all';

export function getBeaCodeDisplayName(
  beacode: { id: string; title: string } | null | undefined
): string {
  return beacode ? `${beacode.title} (${beacode.id.split('_')[1]})` : '';
}

export function getAnySuppliersInDataRows(
  dataRows: Array<ValueMappingRow>
): boolean {
  return dataRows.some(
    (row) =>
      row[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY] !== null
  );
}
